import './Header.css';

import User from 'utils/user.util';
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Assets
import { ReactComponent as MenuIcon } from 'assets/icons/menu_icon.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close_icon.svg';
import { ReactComponent as PersonIcon } from 'assets/icons/person_icon.svg';
import { ReactComponent as ContactIcon } from 'assets/icons/consent_icon.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search_icon.svg';
import { ReactComponent as InventoryIcon } from 'assets/icons/database_icon.svg';
import { ReactComponent as UnifyIcon } from 'assets/icons/unify_icon.svg';
import { ReactComponent as RewardsIcon } from 'assets/icons/star_icon_24.svg';
import { ReactComponent as ReportsIcon } from 'assets/icons/chart_icon.svg';
import { ReactComponent as SettingIcon } from 'assets/icons/settings_icon.svg';
import { ReactComponent as LogoutIcon } from 'assets/icons/logout_icon.svg';
import { ReactComponent as CampaignIcon } from 'assets/icons/bolt_icon.svg';

function Header({ isLoggedIn = false }) {
    const { t } = useTranslation();
    const { i18n } = useTranslation();
    const languages = {
        en: { nativeName: 'EN' },
        fr: { nativeName: 'FR' },
    };
    const [isAdmin, setIsAdmin] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [languageToDisplayKey, setLanguageToDisplayKey] = useState('en');
    const [menuOpen, setMenuOpen] = useState(false);
    const hamburgerRef = useRef(null);
    const panelRef = useRef(null);

    useEffect(() => {
        const currentUser = User.getUser();
        setCurrentUser(currentUser);

        if (currentUser && (currentUser.role === 'superadmin' || currentUser.role === 'admin')) {
            setIsAdmin(true);
        }

        if (currentUser && currentUser.role === 'superadmin') {
            setIsSuperAdmin(true);
        }
    }, []);

    useEffect(() => {
        setLanguageToDisplayKey(
            Object.keys(languages).find((lng) => lng !== i18n.resolvedLanguage)
        );
    }, [i18n.resolvedLanguage]);

    useEffect(() => {
        if (menuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [menuOpen]);

    const closeMenu = useCallback(() => setMenuOpen(false), []);

    useEffect(() => {
        if (menuOpen) {
            panelRef.current?.focus();
            const handleKeyDown = (e) => {
                if (e.key === 'Escape') {
                    closeMenu();
                    hamburgerRef.current?.focus();
                }
            };
            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [menuOpen, closeMenu]);

    return (
        <header>
            <nav className="navbar bg-dark navbar-dark">
                <div className="container">
                    <div className="d-flex align-items-center gap-2">
                        {isLoggedIn && (
                            <button
                                className="nav-hamburger"
                                onClick={() => setMenuOpen(true)}
                                ref={hamburgerRef}
                                aria-label="Open navigation menu"
                                aria-expanded={menuOpen}
                            >
                                <MenuIcon className="nav-hamburger-icon" />
                            </button>
                        )}
                        <Link to="/" className="navbar-brand mb-0">
                            RETAIL CONNECTION PORTAL
                        </Link>
                    </div>

                    <div className="nav-settings">
                        <ul className="navbar-nav flex-row align-items-center gap-3">
                            {isLoggedIn ? (
                                <>
                                    <li className="nav-item">
                                        <a
                                            className="nav-link lang-selector"
                                            href="#"
                                            onClick={(e) => {
                                                i18n.changeLanguage(languageToDisplayKey);
                                                e.preventDefault();
                                            }}
                                        >
                                            {languages[languageToDisplayKey].nativeName}
                                        </a>
                                    </li>
                                    {currentUser && (
                                        <li className="nav-item">
                                            <span className="nav-link nav-username">
                                                {currentUser.name}
                                            </span>
                                        </li>
                                    )}
                                </>
                            ) : (
                                <li className="nav-item">
                                    <a
                                        className="nav-link lang-selector"
                                        href="#"
                                        onClick={(e) => {
                                            i18n.changeLanguage(languageToDisplayKey);
                                            e.preventDefault();
                                        }}
                                    >
                                        {languages[languageToDisplayKey].nativeName}
                                    </a>
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            </nav>

            {isLoggedIn && (
                <>
                    <div
                        className={`nav-overlay-backdrop${menuOpen ? ' open' : ''}`}
                        onClick={closeMenu}
                        aria-hidden="true"
                    />
                    <div
                        className={`nav-overlay-panel${menuOpen ? ' open' : ''}`}
                        ref={panelRef}
                        tabIndex={-1}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('navigation_menu')}
                    >
                        <div className="nav-overlay-header">
                            <span className="nav-overlay-title">RETAIL CONNECTION PORTAL</span>
                            <button
                                className="nav-overlay-close"
                                onClick={closeMenu}
                                aria-label="Close navigation menu"
                            >
                                <CloseIcon className="nav-overlay-close-icon" />
                            </button>
                        </div>

                        {currentUser && (
                            <div className="nav-overlay-user">
                                <PersonIcon className="nav-overlay-item-icon" />
                                <span>{currentUser.name}</span>
                            </div>
                        )}

                        <nav className="nav-overlay-nav">
                            <Link
                                to="/contactform"
                                className="nav-overlay-item"
                                onClick={closeMenu}
                            >
                                <ContactIcon className="nav-overlay-item-icon" />
                                <span>{t('contact_webform')}</span>
                            </Link>
                            <Link
                                to="/tokenfinder"
                                className="nav-overlay-item"
                                onClick={closeMenu}
                            >
                                <SearchIcon className="nav-overlay-item-icon" />
                                <span>{t('egift_card_finder')}</span>
                            </Link>

                            {isAdmin && (
                                <>
                                    <div className="nav-overlay-divider" />
                                    <Link
                                        to="/giftcardinventory"
                                        className="nav-overlay-item"
                                        onClick={closeMenu}
                                    >
                                        <InventoryIcon className="nav-overlay-item-icon" />
                                        <span>{t('gift_card_inventory')}</span>
                                    </Link>
                                    <Link
                                        to="/unification"
                                        className="nav-overlay-item"
                                        onClick={closeMenu}
                                    >
                                        <UnifyIcon className="nav-overlay-item-icon" />
                                        <span>Unification</span>
                                    </Link>
                                    <Link
                                        to="/b2brewards"
                                        className="nav-overlay-item"
                                        onClick={closeMenu}
                                    >
                                        <RewardsIcon className="nav-overlay-item-icon" />
                                        <span>{t('b2b_yearly_rewards')}</span>
                                    </Link>
                                    <Link
                                        to="/reports"
                                        className="nav-overlay-item"
                                        onClick={closeMenu}
                                    >
                                        <ReportsIcon className="nav-overlay-item-icon" />
                                        <span>{t('reports')}</span>
                                    </Link>
                                    <Link
                                        to="/campaigntrigger"
                                        className="nav-overlay-item"
                                        onClick={closeMenu}
                                    >
                                        <CampaignIcon className="nav-overlay-item-icon" />
                                        <span>{t('campaign_list_trigger')}</span>
                                    </Link>
                                </>
                            )}

                            <div className="nav-overlay-divider" />

                            {isAdmin && (
                                <Link
                                    to="/settings"
                                    className="nav-overlay-item"
                                    onClick={closeMenu}
                                >
                                    <SettingIcon className="nav-overlay-item-icon" />
                                    <span>{t('settings')}</span>
                                </Link>
                            )}
                            <Link to="/logout" className="nav-overlay-item" onClick={closeMenu}>
                                <LogoutIcon className="nav-overlay-item-icon" />
                                <span>{t('logout')}</span>
                            </Link>
                        </nav>
                    </div>
                </>
            )}
        </header>
    );
}

export default Header;
